import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { isPageAllowed } from "@/lib/operatorPages";
import { AppShell } from "@/components/AppShell";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import RoleSelectionPage from "@/pages/RoleSelectionPage";
import AuthCallback from "@/pages/AuthCallback";
import Overview from "@/pages/Overview";
import Dashboard from "@/pages/Dashboard";
import EmployeeForm from "@/pages/EmployeeForm";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Verify from "@/pages/Verify";
import RfpDownloadsPage from "@/pages/RfpDownloadsPage";
import EmployeePortal from "@/pages/EmployeePortal";
import PendingDocumentsPage from "@/pages/PendingDocumentsPage";
import PhotosUploadPage from "@/pages/PhotosUploadPage";
import GroupsPage from "@/pages/GroupsPage";
import ImportStatusPage from "@/pages/ImportStatusPage";
import AutodichiarazioniPage from "@/pages/AutodichiarazioniPage";
import HireDateCheckPage from "@/pages/HireDateCheckPage";
import PrintHistoryPage from "@/pages/PrintHistoryPage";
import DocumentAuditPage from "@/pages/DocumentAuditPage";
import ComplianceDashboardPage from "@/pages/ComplianceDashboardPage";
import DeliverySettingsPage from "@/pages/DeliverySettingsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import EmployeeUploadsPage from "@/pages/EmployeeUploadsPage";
import EmployeeChangesPage from "@/pages/EmployeeChangesPage";
import AdvancesPage from "@/pages/AdvancesPage";
import AdvanceDetailPage from "@/pages/AdvanceDetailPage";
import TfrPage from "@/pages/TfrPage";
import FngPage from "@/pages/FngPage";
import ScadenzePage from "@/pages/ScadenzePage";
import ContractTemplatesPage from "@/pages/ContractTemplatesPage";
import UnilavBulkPage from "@/pages/UnilavBulkPage";
import EmailLogPage from "@/pages/EmailLogPage";
import PendingSignaturesPage from "@/pages/PendingSignaturesPage";
import MissingDocsPage from "@/pages/MissingDocsPage";
import ContractsPage from "@/pages/ContractsPage";
import DocumentsArchivePage from "@/pages/DocumentsArchivePage";
import DeliveryRegisterPage from "@/pages/DeliveryRegisterPage";
import PresenzePage from "@/pages/PresenzePage";
import CantieriPage from "@/pages/CantieriPage";
import CantieriMappaPage from "@/pages/CantieriMappaPage";
import PayslipCheckPage from "@/pages/PayslipCheckPage";
import WelcomeDocsPage from "@/pages/WelcomeDocsPage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import TimbraturaPage from "@/pages/TimbraturaPage";
import CantiereAttivitaPage from "@/pages/CantiereAttivitaPage";
import FeriePage from "@/pages/FeriePage";
import StraordinariPage from "@/pages/StraordinariPage";
import PianificazionePage from "@/pages/PianificazionePage";
import CalendarioPresenzePage from "@/pages/CalendarioPresenzePage";
import AssegnazioniPage from "@/pages/AssegnazioniPage";
import RichiesteDipendentePage from "@/pages/RichiesteDipendentePage";
import RlsIntegrationPage from "@/pages/RlsIntegrationPage";
import OreDipendentePage from "@/pages/OreDipendentePage";

// Admin OR operator with page grants (operator_pages)
const Staff = ({ children }) => (
    <ProtectedRoute requireRole={["admin", "operator"]}>
        <OperatorGuard>
            <AppShell>{children}</AppShell>
        </OperatorGuard>
    </ProtectedRoute>
);

const OperatorGuard = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    if (user && user.role === "operator" && !isPageAllowed(user, location.pathname)) {
        return <Navigate to="/portale" replace />;
    }
    return children;
};

/**
 * AppRouter — must check the URL hash for `session_id` synchronously during
 * render (not in useEffect) so the AuthCallback runs BEFORE any ProtectedRoute
 * evaluates the /auth/me endpoint. This prevents the "no session yet → redirect
 * to /login" race condition when returning from Google OAuth.
 */
function AppRouter() {
    const location = useLocation();
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accesso" element={<RoleSelectionPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify/:matricola" element={<Verify />} />
            <Route path="/documenti-rfp" element={<RfpDownloadsPage />} />
            <Route
                path="/portale"
                element={
                    <ProtectedRoute requireRole={["employee", "operator"]}>
                        <EmployeePortal />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/portale/richieste"
                element={
                    <ProtectedRoute requireRole={["employee", "operator"]}>
                        <RichiesteDipendentePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/portale/ore"
                element={
                    <ProtectedRoute requireRole={["employee", "operator"]}>
                        <OreDipendentePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/timbratura"
                element={
                    <ProtectedRoute requireRole={["employee", "operator", "admin"]}>
                        <TimbraturaPage />
                    </ProtectedRoute>
                }
            />

            {/* Staff routes (admin + operator): everything except the admin-only areas below */}
            <Route path="/" element={<Staff><Overview /></Staff>} />
            <Route path="/scadenze" element={<Staff><ScadenzePage /></Staff>} />
            <Route path="/registro-email" element={<Staff><EmailLogPage /></Staff>} />
            <Route path="/dipendenti" element={<Staff><Dashboard /></Staff>} />
            <Route path="/dipendenti/nuovo" element={<Staff><EmployeeForm /></Staff>} />
            <Route path="/dipendenti/:id" element={<Staff><EmployeeDetail /></Staff>} />
            <Route path="/dipendenti/:id/modifica" element={<Staff><EmployeeForm /></Staff>} />
            <Route path="/gruppi" element={<Staff><GroupsPage /></Staff>} />
            <Route path="/notifiche/modifiche-dati" element={<Staff><EmployeeChangesPage /></Staff>} />
            <Route path="/documenti/in-attesa" element={<Staff><PendingDocumentsPage /></Staff>} />
            <Route path="/documenti/da-firmare" element={<Staff><PendingSignaturesPage /></Staff>} />
            <Route path="/documenti/dipendenti" element={<Staff><EmployeeUploadsPage /></Staff>} />
            <Route path="/documenti/mancanti" element={<Staff><MissingDocsPage /></Staff>} />
            <Route path="/documenti/contratti" element={<Staff><ContractsPage /></Staff>} />
            <Route path="/documenti/archivio" element={<Staff><DocumentsArchivePage /></Staff>} />
            <Route path="/documenti/registro-consegne" element={<Staff><DeliveryRegisterPage /></Staff>} />
            <Route path="/documenti/buste-paga" element={<Staff><PayslipCheckPage /></Staff>} />
            <Route path="/documenti/benvenuto" element={<Staff><WelcomeDocsPage /></Staff>} />
            <Route path="/dipendenti/attivita" element={<Staff><ActivityLogPage /></Staff>} />
            <Route path="/modelli-contratto" element={<Staff><ContractTemplatesPage /></Staff>} />
            <Route path="/foto/upload" element={<Staff><PhotosUploadPage /></Staff>} />

            {/* Pages granted to operators via operator_pages (admin always allowed) */}
            <Route path="/conformita" element={<Staff><ComplianceDashboardPage /></Staff>} />
            <Route path="/impostazioni/consegna" element={<Staff><DeliverySettingsPage /></Staff>} />
            <Route path="/documenti/storico" element={<Staff><DocumentAuditPage /></Staff>} />
            <Route path="/migrazione/stato" element={<Staff><ImportStatusPage /></Staff>} />
            <Route path="/documenti/autodichiarazioni" element={<Staff><AutodichiarazioniPage /></Staff>} />
            <Route path="/personale/verifica-date" element={<Staff><HireDateCheckPage /></Staff>} />
            <Route path="/storico-stampe" element={<Staff><PrintHistoryPage /></Staff>} />
            <Route path="/acconti" element={<Staff><AdvancesPage /></Staff>} />
            <Route path="/acconti/tfr" element={<Staff><TfrPage /></Staff>} />
            <Route path="/acconti/fng" element={<Staff><FngPage /></Staff>} />
            <Route path="/acconti/:id" element={<Staff><AdvanceDetailPage /></Staff>} />
            <Route path="/presenze" element={<Staff><PresenzePage /></Staff>} />
            <Route path="/presenze/cantieri" element={<Staff><CantieriPage /></Staff>} />
            <Route path="/presenze/cantieri/mappa" element={<Staff><CantieriMappaPage /></Staff>} />
            <Route path="/presenze/cantieri/:wsId" element={<Staff><CantiereAttivitaPage /></Staff>} />
            <Route path="/presenze/pianificazione" element={<Staff><PianificazionePage /></Staff>} />
            <Route path="/presenze/calendario" element={<Staff><CalendarioPresenzePage /></Staff>} />
            <Route path="/presenze/assegnazioni" element={<Staff><AssegnazioniPage /></Staff>} />
            <Route path="/presenze/ferie" element={<Staff><FeriePage /></Staff>} />
            <Route path="/presenze/straordinari" element={<Staff><StraordinariPage /></Staff>} />
            <Route path="/integrazione-rls" element={<Staff><RlsIntegrationPage /></Staff>} />
            <Route path="/unilav" element={<Staff><UnilavBulkPage /></Staff>} />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <CompanyProvider>
                    <AuthProvider>
                        <AppRouter />
                        <Toaster position="bottom-right" richColors closeButton duration={2500} visibleToasts={3} />
                    </AuthProvider>
                </CompanyProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
